import Color from 'color';

import {SemanticLegendSettings, YAxisValues} from '../../types';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from '../SemanticLegendDefaults';
import {SemanticLegendPalette} from './colorPalettes';
import {getColorFromValue} from './getDefaultBucketColors';

// Performance optimization: Cache for color calculations
const colorCache = new Map<string, string>();
const MAX_CACHE_SIZE = 1000;

// Helper function to create a cache key from relevant properties
function createCacheKey(
  runName: string,
  yAxisValue: YAxisValues | undefined,
  key: string | undefined,
  buckets: any,
  categories: string[] | undefined,
  bucketCustomizations: any,
  excludedRunColor: string | undefined,
  palette: SemanticLegendPalette,
  alpha: number
): string {
  // Create a compact cache key
  return JSON.stringify({
    runName,
    yAxisValue,
    key,
    buckets: buckets?.map((b: any) => ({
      bucketMax: b.bucketMax,
      color: b.color,
    })),
    categories,
    bucketCustomizations,
    excludedRunColor,
    palette,
    alpha,
  });
}

export function getColorFromSemanticLegend(
  runName: string,
  semanticLegendSettings: SemanticLegendSettings,
  semanticLegendPalette: SemanticLegendPalette,
  alpha?: number
) {
  // Early return for invalid settings
  if (!semanticLegendSettings) {
    return Color(DEFAULT_SEMANTIC_LEGEND_SETTINGS.excludedRunColor)
      .alpha(alpha || 1)
      .rgb()
      .string();
  }

  const finalAlpha = alpha !== undefined ? alpha : 1;

  // Check cache first for performance optimization
  const cacheKey = createCacheKey(
    runName,
    semanticLegendSettings.yAxisValue,
    semanticLegendSettings.key,
    semanticLegendSettings.buckets,
    semanticLegendSettings.categories,
    semanticLegendSettings.bucketCustomizations,
    semanticLegendSettings.excludedRunColor,
    semanticLegendPalette,
    finalAlpha
  );

  const cachedResult = colorCache.get(cacheKey);
  if (cachedResult) {
    return cachedResult;
  }

  // Cache frequently accessed properties
  const yAxisValue = semanticLegendSettings.yAxisValue;
  const buckets = semanticLegendSettings.buckets;
  const categories = semanticLegendSettings.categories;
  const bucketCustomizations = semanticLegendSettings.bucketCustomizations;
  const excludedRunColor = semanticLegendSettings.excludedRunColor;

  const configValuesMap = new Map<string, any>();
  semanticLegendSettings.configValuesForRun?.forEach(config => {
    configValuesMap.set(config.runName, config);
  });

  const metricStatsMap = new Map<string, any>();
  semanticLegendSettings.metricStatisticsForRun?.forEach(stat => {
    metricStatsMap.set(stat.run_name, stat);
  });

  let colorString = excludedRunColor;
  let bucketIndex = -1;

  // If the y-axis value is config categories, we need to get the config value for the run and find an exact match for the category.
  if (yAxisValue === YAxisValues.Config_Categories) {
    const configVal = configValuesMap.get(runName);

    if (configVal && categories) {
      bucketIndex = categories.findIndex(
        category => configVal.value === category
      );

      if (bucketIndex !== -1) {
        const customColor = bucketCustomizations?.[bucketIndex]?.color;
        if (customColor) {
          colorString = customColor;
        } else {
          colorString = getColorFromValue(
            bucketIndex,
            categories.length,
            semanticLegendPalette
          );
        }
      }
    }
    // Otherwise, we're using bucketing and need to find the bucket that the run's metric/config value falls into.
  } else {
    const {bucketIndex, val} = getBucketIndexForNumericBuckets(
      semanticLegendSettings,
      runName,
      configValuesMap,
      metricStatsMap
    );

    // If the run's metric value is in a bucket, use the bucket's color.
    if (bucketIndex !== -1 && buckets) {
      const customColor = bucketCustomizations?.[bucketIndex]?.color;
      const bucketColor = buckets[bucketIndex]?.color;
      colorString = customColor || bucketColor || excludedRunColor;
    } else if (buckets && buckets.length > 0) {
      // If the run's metric value is not in a bucket and it's greater than the last bucket's max, use the last bucket's color.
      const lastBucket = buckets[buckets.length - 1];

      if (val > (lastBucket?.bucketMax ?? 0)) {
        colorString = lastBucket?.color || excludedRunColor;
      }
    }
  }

  // Single Color object creation and processing
  const result = Color(colorString || excludedRunColor)
    .alpha(finalAlpha)
    .rgb()
    .string();

  // Cache the result for future use
  colorCache.set(cacheKey, result);

  // Limit cache size to prevent memory leaks
  if (colorCache.size > MAX_CACHE_SIZE) {
    const firstKey = colorCache.keys().next().value;
    if (firstKey) {
      colorCache.delete(firstKey);
    }
  }

  return result;
}

// Export function to clear cache if needed (for testing or memory management)
export function clearColorCache() {
  colorCache.clear();
}

// Export function to get cache stats (for debugging)
export function getColorCacheStats() {
  return {
    size: colorCache.size,
    maxSize: MAX_CACHE_SIZE,
  };
}

function getBucketIndexForNumericBuckets(
  semanticLegendSettings: SemanticLegendSettings,
  runName: string,
  configValuesMap: Map<string, any>,
  metricStatsMap: Map<string, any>
) {
  const yAxisValue = semanticLegendSettings.yAxisValue;
  let val = 0;

  if (yAxisValue === YAxisValues.Config_Number) {
    const configVal = configValuesMap.get(runName);
    if (configVal != null) {
      val = configVal.value;
    } else {
      return {bucketIndex: -1, val: 0};
    }
  } else {
    const metricStat = metricStatsMap.get(runName);
    // If the run is not in the metric statistics, use the excluded run color.
    if (metricStat == null) {
      return {bucketIndex: -1, val: 0};
    } else {
      switch (yAxisValue) {
        case 'min':
          val = metricStat.min;
          break;
        case 'max':
          val = metricStat.max;
          break;
        case 'last':
          val = metricStat.last;
          break;
        default:
          break;
      }
    }
  }

  // find the bucket that the run's metric value falls into.
  const buckets = semanticLegendSettings.buckets;
  if (!buckets) {
    return {bucketIndex: -1, val: 0};
  }

  return {
    bucketIndex: buckets.findIndex(bucket => {
      return val <= (bucket.bucketMax ?? 0);
    }),
    val: val,
  };
}
